import { NavLink, useLocation } from "react-router-dom";
import { LayoutGrid, ClipboardCheck, Users, AlertTriangle, MoreHorizontal } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * Bottom tab bar (mobile only) for field roles — FH / Assistant / Counter.
 * Thumb-first navigation with 5 core tabs. Hidden on desktop (md:hidden).
 * Also hidden for Owner/Head/PM/PO/Client — they use the top nav strip.
 */
export default function BottomTabBar() {
    const { user } = useAuth();
    const location = useLocation();
    const eff = user?.effective_role || user?.role;
    const isFieldRole = eff === "FACILITY_HEAD" || eff === "ASSISTANT";
    if (!isFieldRole) return null;

    // Hide on login-related routes
    if (location.pathname.startsWith("/login") || location.pathname.startsWith("/change-password")) return null;

    const siteIds = user?.site_ids || [];
    const primarySiteId = siteIds[0] || "";
    const checklistTo = primarySiteId ? `/sites/${primarySiteId}/run` : "/";
    const attendanceTo = primarySiteId ? `/sites/${primarySiteId}/attendance` : "/";

    const tabs = [
        { to: "/", label: "Sites", icon: LayoutGrid, tid: "tab-sites", end: true },
        { to: checklistTo, label: "Checklist", icon: ClipboardCheck, tid: "tab-checklist" },
        { to: attendanceTo, label: "Attendance", icon: Users, tid: "tab-attendance" },
        { to: "/service-requests", label: "Requests", icon: AlertTriangle, tid: "tab-requests" },
        { to: "/tutorial", label: "More", icon: MoreHorizontal, tid: "tab-more" },
    ];

    function isActive(to, end) {
        if (end) return location.pathname === to;
        return location.pathname === to || location.pathname.startsWith(to + "/");
    }

    return (
        <nav
            className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(30,51,85,0.06)]"
            data-testid="bottom-tab-bar"
        >
            <div className="grid grid-cols-5">
                {tabs.map((t) => {
                    const active = isActive(t.to, t.end);
                    return (
                        <NavLink
                            key={t.tid}
                            to={t.to}
                            end={t.end}
                            data-testid={t.tid}
                            className={`flex flex-col items-center justify-center py-2 min-h-[56px] transition-colors ${active ? "text-[#E02B2B]" : "text-gray-500 hover:text-[#1E3355]"}`}
                        >
                            <t.icon className="w-5 h-5" />
                            <span className={`text-[10px] mt-0.5 ${active ? "font-semibold" : ""}`}>{t.label}</span>
                        </NavLink>
                    );
                })}
            </div>
        </nav>
    );
}
